import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  useReactTable, getCoreRowModel, flexRender,
} from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  Building2, Plus, Trash2, Pencil, Search, Download, Check, X,
  ChevronUp, ChevronDown, ChevronsUpDown, Settings2, ChevronLeft, ChevronRight, Loader2,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useAppearance } from "../contexts/AppearanceContext";
import { usePermissions } from "../hooks/usePermissions";
import { useTablePreferences } from "../hooks/useTablePreferences";
import EmpresaFormDialog from "../components/EmpresaFormDialog";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import { formatDateTime } from "../lib/dateUtils";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 100;

export default function EmpresasPage() {
  const { t } = useTranslation();
  const appearance = useAppearance();
  const { can } = usePermissions();
  const tc500 = appearance.colors?.["500"] || "#10B981";
  const tc50 = appearance.colors?.["50"] || "#ECFDF5";

  const { prefs, updatePrefs, loaded: prefsLoaded } = useTablePreferences("empresas", { sortBy: "created_at", sortDir: "desc", columnVisibility: {} });

  const [empresas, setEmpresas] = useState([]);
  const [totalEmpresas, setTotalEmpresas] = useState(0);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const sortBy = prefs.sortBy;
  const sortDir = prefs.sortDir;
  const columnVisibility = prefs.columnVisibility;

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [draggedColId, setDraggedColId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkIsCustom, setBulkIsCustom] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setSearchDebounced(search); setPage(0); }, 350); return () => clearTimeout(t); }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, cfRes] = await Promise.all([
        axios.get(`${API}/empresas`, { params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE, search: searchDebounced, sort_by: sortBy, sort_dir: sortDir }, withCredentials: true }),
        page === 0 ? axios.get(`${API}/custom-fields?module=empresas`, { withCredentials: true }) : null,
      ].filter(Boolean));
      setEmpresas(eRes.data?.data || []);
      setTotalEmpresas(eRes.data?.total ?? 0);
      if (cfRes) setCustomFields(cfRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, searchDebounced, sortBy, sortDir]);

  useEffect(() => { if (prefsLoaded) fetchData(); }, [fetchData, prefsLoaded]);
  useEffect(() => { setRowSelection({}); }, [empresas]);

  const totalPages = Math.max(1, Math.ceil(totalEmpresas / PAGE_SIZE));

  const handleSort = (col) => {
    if (sortBy === col) updatePrefs({ sortDir: sortDir === "asc" ? "desc" : "asc" });
    else updatePrefs({ sortBy: col, sortDir: "asc" });
    setPage(0);
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
    return sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5 text-slate-700" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-700" />;
  };

  const columns = useMemo(() => {
    const cols = [
      {
        id: "select", size: 40, enableSorting: false,
        header: ({ table }) => <Checkbox data-testid="select-all-checkbox" checked={table.getIsAllPageRowsSelected()} onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)} />,
        cell: ({ row }) => <Checkbox data-testid={`select-row-${row.original.id}`} checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(!!v)} onClick={(e) => e.stopPropagation()} />,
      },
      {
        id: "nome", accessorKey: "nome", header: "Nome", size: 280,
        cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{ backgroundColor: tc500 }}>
              {row.original.nome?.charAt(0).toUpperCase()}
            </div>
            <span className="font-medium text-slate-900 truncate">{row.original.nome}</span>
          </div>
        ),
      },
      {
        id: "created_at", accessorKey: "created_at", header: "Criado em", size: 160,
        cell: ({ getValue }) => <span className="text-sm text-slate-500">{formatDateTime(getValue())}</span>,
      },
    ];
    customFields.forEach(cf => {
      cols.push({
        id: `cf_${cf.field_name}`, header: cf.field_label, size: 160, enableSorting: false,
        cell: ({ row }) => {
          const val = row.original.custom_fields?.[cf.field_name];
          if (!val) return <span className="text-slate-300">-</span>;
          return <span className="text-sm text-slate-600">{String(val)}</span>;
        },
      });
    });
    cols.push({
      id: "actions", size: 80, enableSorting: false, header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-0.5 justify-end">
          {can("empresas", "edit") && <Tooltip><TooltipTrigger asChild><Button data-testid={`edit-empresa-${row.original.id}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><Pencil className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent>Editar</TooltipContent></Tooltip>}
          {can("empresas", "delete") && <Tooltip><TooltipTrigger asChild><Button data-testid={`delete-empresa-${row.original.id}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}><Trash2 className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent>Excluir</TooltipContent></Tooltip>}
        </div>
      ),
    });
    return cols;
  }, [customFields, tc500, can]);

  const resolvedColumnOrder = useMemo(() => {
    const saved = prefs.columnOrder || [];
    if (saved.length === 0) return [];
    const allIds = columns.map(c => c.id);
    const valid = saved.filter(id => allIds.includes(id));
    const missing = allIds.filter(id => !valid.includes(id));
    return [...valid, ...missing];
  }, [prefs.columnOrder, columns]);

  const table = useReactTable({
    data: empresas, columns, getCoreRowModel: getCoreRowModel(),
    manualSorting: true, manualPagination: true, pageCount: totalPages,
    state: { rowSelection, columnVisibility, columnOrder: resolvedColumnOrder.length > 0 ? resolvedColumnOrder : undefined },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: (updater) => { const next = typeof updater === "function" ? updater(columnVisibility) : updater; updatePrefs({ columnVisibility: next }); },
    enableRowSelection: true, getRowId: (row) => row.id,
  });

  const selectedIds = Object.keys(rowSelection).filter(k => rowSelection[k]);
  const hasSelection = selectedIds.length > 0;

  const openNew = () => { setEditItem(null); setDialogOpen(true); };
  const openEdit = (e) => { setEditItem(e); setDialogOpen(true); };
  const confirmDelete = async () => { if (!deleteTarget) return; try { await axios.delete(`${API}/empresas/${deleteTarget.id}`, { withCredentials: true }); fetchData(); } catch (err) { console.error(err); } setDeleteTarget(null); };

  const handleBulkDelete = async () => {
    try { await axios.post(`${API}/empresas/bulk-delete`, { ids: selectedIds }, { withCredentials: true }); toast.success(`${selectedIds.length} empresa(s) excluida(s)`); setRowSelection({}); fetchData(); } catch { toast.error("Erro ao excluir"); }
    setBulkDeleteOpen(false);
  };
  const handleBulkEdit = async () => {
    if (!bulkField || bulkValue === "") return;
    try { await axios.post(`${API}/empresas/bulk-update`, { ids: selectedIds, field: bulkField, value: bulkValue, is_custom: bulkIsCustom }, { withCredentials: true }); toast.success(`${selectedIds.length} empresa(s) atualizada(s)`); setRowSelection({}); fetchData(); } catch { toast.error("Erro ao atualizar"); }
    setBulkEditOpen(false); setBulkField(""); setBulkValue("");
  };
  const handleExport = async () => {
    try {
      const XLSX = await import("xlsx");
      const allRes = await axios.get(`${API}/empresas`, { params: { skip: 0, limit: 99999, search: searchDebounced, sort_by: sortBy, sort_dir: sortDir }, withCredentials: true });
      const all = allRes.data?.data || [];
      const rows = all.map(e => { const row = { "Nome": e.nome, "Data de Criacao": formatDateTime(e.created_at) }; customFields.forEach(cf => { row[cf.field_label] = e.custom_fields?.[cf.field_name] ?? ""; }); return row; });
      const ws = XLSX.utils.json_to_sheet(rows); ws["!cols"] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 15) }));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Empresas"); XLSX.writeFile(wb, `empresas_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) { console.error(err); toast.error("Erro ao exportar"); }
    setExportDialogOpen(false);
  };

  const editableFields = useMemo(() => {
    const fields = [{ key: "nome", label: "Nome", isCustom: false }];
    customFields.forEach(cf => { fields.push({ key: cf.field_name, label: cf.field_label, isCustom: true }); });
    return fields;
  }, [customFields]);

  const toggleableColumns = useMemo(() => columns.filter(c => c.id !== "select" && c.id !== "actions").map(c => ({ id: c.id, label: c.header || c.id })), [columns]);

  return (
    <div data-testid="empresas-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}><Building2 className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              {t("empresas.title")}
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{totalEmpresas}</Badge>
              {hasSelection && <Tooltip><TooltipTrigger asChild><Badge data-testid="bulk-selection-count" className="text-white text-sm font-medium px-3 py-1 cursor-default" style={{ backgroundColor: tc500 }}>{selectedIds.length}</Badge></TooltipTrigger><TooltipContent>{selectedIds.length} empresa(s) selecionada(s)</TooltipContent></Tooltip>}
            </h1>
            <p className="text-slate-500 mt-0.5">{t("empresas.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasSelection && (
            <>
              {can("empresas", "edit") && <Tooltip><TooltipTrigger asChild><Button data-testid="bulk-edit-btn" size="icon" className="h-9 w-9 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} onClick={() => setBulkEditOpen(true)}><Pencil className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Editar campo em lote</TooltipContent></Tooltip>}
              {can("empresas", "delete") && <Tooltip><TooltipTrigger asChild><Button data-testid="bulk-delete-btn" size="icon" className="h-9 w-9 text-white bg-rose-500 hover:bg-rose-600" onClick={() => setBulkDeleteOpen(true)}><Trash2 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Excluir selecionados</TooltipContent></Tooltip>}
              <Tooltip><TooltipTrigger asChild><Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setRowSelection({})}><X className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Limpar selecao</TooltipContent></Tooltip>
              <div className="w-px h-6 bg-slate-200" />
            </>
          )}
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="empresa-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t("empresas.search")} className="pl-9 w-full sm:w-56 h-9 text-sm" /></div>
          <Popover>
            <PopoverTrigger asChild><Button data-testid="column-visibility-btn" variant="outline" size="icon" className="h-9 w-9 text-slate-600"><Settings2 className="w-4 h-4" /></Button></PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 mb-2">Colunas visiveis</p>
              {toggleableColumns.map(col => { const isVisible = table.getColumn(col.id)?.getIsVisible() ?? true; return (<label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm text-slate-700"><Checkbox checked={isVisible} onCheckedChange={(v) => table.getColumn(col.id)?.toggleVisibility(!!v)} />{col.label}</label>); })}
            </PopoverContent>
          </Popover>
          {can("empresas", "export") && <Tooltip><TooltipTrigger asChild><Button data-testid="empresa-export-btn" variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setExportDialogOpen(true)}><Download className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("ui.exportar")}</TooltipContent></Tooltip>}
          {can("empresas", "create") && <Tooltip><TooltipTrigger asChild><Button data-testid="add-empresa-btn" onClick={openNew} size="icon" style={{ backgroundColor: tc500 }} className="text-white h-9 w-9 hover:opacity-90"><Plus className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("empresas.new")}</TooltipContent></Tooltip>}
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} className="border-b border-slate-200 bg-slate-50/80">
                  {hg.headers.map(header => { const canSort = header.column.columnDef.enableSorting !== false && header.column.columnDef.accessorKey; const isDraggable = header.id !== "select" && header.id !== "actions"; return (
                    <th key={header.id} style={{ width: header.getSize() }}
                      draggable={isDraggable}
                      onDragStart={(e) => { if (!isDraggable) return; setDraggedColId(header.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={(e) => { if (isDraggable) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                      onDrop={(e) => { e.preventDefault(); if (!draggedColId || draggedColId === header.id || !isDraggable) return; const cur = table.getState().columnOrder?.length > 0 ? [...table.getState().columnOrder] : table.getAllLeafColumns().map(c => c.id); const fi = cur.indexOf(draggedColId); const ti = cur.indexOf(header.id); if (fi !== -1 && ti !== -1) { cur.splice(fi, 1); cur.splice(ti, 0, draggedColId); updatePrefs({ columnOrder: cur }); } setDraggedColId(null); }}
                      onDragEnd={() => setDraggedColId(null)}
                      className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${canSort ? "cursor-pointer select-none hover:text-slate-700" : ""} ${isDraggable && !canSort ? "cursor-grab" : ""} ${draggedColId === header.id ? "opacity-50" : ""}`}
                      onClick={() => canSort && handleSort(header.column.columnDef.accessorKey)}>
                      <div className="flex items-center gap-1.5">{flexRender(header.column.columnDef.header, header.getContext())}{canSort && <SortIcon col={header.column.columnDef.accessorKey} />}</div>
                    </th>); })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? [...Array(8)].map((_, i) => (<tr key={i} className="border-b border-slate-100">{columns.filter(c => table.getColumn(c.id)?.getIsVisible() !== false).map((c, j) => (<td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>))}</tr>))
              : table.getRowModel().rows.length === 0 ? (<tr><td colSpan={columns.length} className="text-center py-16"><div className="flex flex-col items-center text-slate-400"><Building2 className="w-10 h-10 mb-3 text-slate-300" /><p className="text-sm">{empresas.length === 0 ? t("empresas.empty") : t("empresas.not_found")}</p></div></td></tr>)
              : table.getRowModel().rows.map(row => (
                <tr key={row.id} data-testid={`empresa-row-${row.original.id}`} className={`border-b border-slate-100 transition-colors ${can("empresas", "edit") ? "cursor-pointer" : ""} ${row.getIsSelected() ? "bg-slate-50" : "hover:bg-slate-50/50"}`} onClick={() => can("empresas", "edit") && openEdit(row.original)}>
                  {row.getVisibleCells().map(cell => (<td key={cell.id} className="px-3 py-2.5">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>))}
                </tr>))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <p className="text-xs text-slate-500">{totalEmpresas > 0 ? `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, totalEmpresas)} de ${totalEmpresas}` : "Nenhum resultado"}</p>
          <div className="flex items-center gap-1">
            <Button data-testid="prev-page-btn" variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            {[...Array(Math.min(totalPages, 5))].map((_, i) => { let pn; if (totalPages <= 5) pn = i; else if (page < 3) pn = i; else if (page > totalPages - 4) pn = totalPages - 5 + i; else pn = page - 2 + i; return (<Button key={pn} variant={pn === page ? "default" : "outline"} size="sm" className={`h-8 w-8 p-0 text-xs ${pn === page ? "text-white" : ""}`} style={pn === page ? { backgroundColor: tc500 } : {}} onClick={() => setPage(pn)}>{pn + 1}</Button>); })}
            <Button data-testid="next-page-btn" variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <EmpresaFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editItem={editItem} onSaved={() => { setDialogOpen(false); fetchData(); }} />
      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)} title={t("empresas.deleteTitle")} description={t("empresas.delete_confirm", { nome: deleteTarget?.nome })} onConfirm={confirmDelete} />

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="font-heading flex items-center gap-2 text-rose-600"><Trash2 className="w-5 h-5" /> Excluir {selectedIds.length} empresa(s)</AlertDialogTitle><AlertDialogDescription>Esta acao ira excluir permanentemente as empresas selecionadas. Deseja continuar?</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction data-testid="confirm-bulk-delete" onClick={handleBulkDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Excluir {selectedIds.length} empresa(s)</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Pencil className="w-5 h-5" style={{ color: tc500 }} />Editar campo em lote</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">A alteracao sera aplicada a {selectedIds.length} empresa(s).</p>
            <div><Label className="text-sm text-slate-700">Campo</Label>
              <Select value={bulkField ? `${bulkIsCustom ? "cf:" : ""}${bulkField}` : "__none__"} onValueChange={(v) => { if (v === "__none__") { setBulkField(""); setBulkIsCustom(false); return; } if (v.startsWith("cf:")) { setBulkField(v.slice(3)); setBulkIsCustom(true); } else { setBulkField(v); setBulkIsCustom(false); } setBulkValue(""); }}>
                <SelectTrigger data-testid="bulk-field-select" className="mt-1"><SelectValue placeholder="Escolha o campo..." /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">Escolha o campo...</SelectItem>{editableFields.map(f => <SelectItem key={f.key} value={f.isCustom ? `cf:${f.key}` : f.key}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {bulkField && <div><Label className="text-sm text-slate-700">Novo valor</Label><Input data-testid="bulk-value-input" value={bulkValue} onChange={e => setBulkValue(e.target.value)} placeholder="Digite o novo valor..." className="mt-1" /></div>}
            <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={() => setBulkEditOpen(false)}>Cancelar</Button><Button data-testid="confirm-bulk-edit" size="sm" disabled={!bulkField || bulkValue === ""} onClick={handleBulkEdit} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Check className="w-4 h-4 mr-2" /> Aplicar</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="font-heading flex items-center gap-2"><Download className="w-5 h-5 text-sky-600" />{t("empresas.export_title")}</AlertDialogTitle><AlertDialogDescription className="text-slate-500">{t("empresas.export_desc")}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel><AlertDialogAction onClick={handleExport} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Download className="w-4 h-4 mr-2" />{t("ui.baixar_excel")}</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
